import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { 
  Users, Eye, Clock, MousePointer, ArrowRight, Monitor, Smartphone, Tablet,
  TrendingUp, Globe, RefreshCw, Activity, ChevronRight, Layers, BarChart3,
  Target, Plus, Trash2, Play, ArrowDown, Filter as FilterIcon, CheckCircle2
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

// Simple heatmap visualization component
const HeatmapVisualization = ({ clicks, width = 800, height = 600 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !clicks.length) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw gradient background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);
    
    // Draw click points as circles with gradient
    clicks.forEach(click => {
      const x = (click.x_percent / 100) * width;
      const y = (click.y_percent / 100) * height;
      
      // Create radial gradient
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 30);
      gradient.addColorStop(0, 'rgba(239, 68, 68, 0.8)');
      gradient.addColorStop(0.5, 'rgba(239, 68, 68, 0.3)');
      gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, 30, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw page outline
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, width, height);
    
  }, [clicks, width, height]);

  return (
    <div className="relative border rounded-lg overflow-hidden bg-white">
      <canvas ref={canvasRef} width={width} height={height} />
      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
        {clicks.length} clicks tracked
      </div>
    </div>
  );
};

// Scroll depth visualization
const ScrollDepthBar = ({ stats }) => {
  if (!stats) return null;
  
  const total = stats.total || 1;
  const depths = [
    { label: '25%', value: stats.scroll_25, color: 'bg-green-500' },
    { label: '50%', value: stats.scroll_50, color: 'bg-yellow-500' },
    { label: '75%', value: stats.scroll_75, color: 'bg-orange-500' },
    { label: '100%', value: stats.scroll_100, color: 'bg-red-500' },
  ];

  return (
    <div className="space-y-2">
      {depths.map(d => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-12 text-sm text-muted-foreground">{d.label}</span>
          <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full ${d.color} transition-all`}
              style={{ width: `${(d.value / total) * 100}%` }}
            />
          </div>
          <span className="w-16 text-sm text-right">{Math.round((d.value / total) * 100)}%</span>
        </div>
      ))}
    </div>
  );
};

export default function VisitorInsights() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [navFlow, setNavFlow] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [selectedDays, setSelectedDays] = useState("30");
  const [heatmapPage, setHeatmapPage] = useState("/");
  const [refreshing, setRefreshing] = useState(false);
  
  // Funnel state
  const [funnels, setFunnels] = useState([]);
  const [funnelPresets, setFunnelPresets] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [funnelAnalysis, setFunnelAnalysis] = useState(null);
  const [funnelDialogOpen, setFunnelDialogOpen] = useState(false);
  const [analyzingFunnel, setAnalyzingFunnel] = useState(false);
  const [newFunnel, setNewFunnel] = useState({
    name: "",
    description: "",
    steps: [
      { name: "", path_pattern: "", order: 1 },
      { name: "", path_pattern: "", order: 2 }
    ]
  });

  useEffect(() => {
    fetchDashboard();
    fetchRealtime();
    fetchFunnels();
    fetchFunnelPresets();
    
    // Refresh realtime data every 30 seconds
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [selectedDays]);

  const fetchDashboard = async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const [dashRes, flowRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard?days=${selectedDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/navigation-flow?days=${selectedDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDashboard(dashRes.data);
      setNavFlow(flowRes.data);
    } catch (error) {
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchHeatmap = async (page) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/analytics/heatmap${page}?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHeatmapData(response.data);
    } catch (error) {
      toast.error("Failed to load heatmap data");
    }
  };

  const fetchRealtime = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/analytics/realtime`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRealtime(response.data);
    } catch (error) {
      console.error("Failed to load realtime data");
    }
  };

  const fetchFunnels = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/analytics/funnels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFunnels(response.data);
    } catch (error) {
      console.error("Failed to load funnels");
    }
  };

  const fetchFunnelPresets = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/analytics/funnels/presets/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFunnelPresets(response.data);
    } catch (error) {
      console.error("Failed to load funnel presets");
    }
  };

  const analyzeFunnel = async (funnelId) => {
    setAnalyzingFunnel(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/analytics/funnels/${funnelId}/analyze?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFunnelAnalysis(response.data);
      setSelectedFunnel(funnelId);
    } catch (error) {
      toast.error("Failed to analyze funnel");
    } finally {
      setAnalyzingFunnel(false);
    }
  };

  const createFunnel = async () => {
    if (!newFunnel.name || newFunnel.steps.some(s => !s.name || !s.path_pattern)) {
      toast.error("Please fill in all funnel details");
      return;
    }
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/analytics/funnels`, newFunnel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Funnel created successfully");
      setFunnelDialogOpen(false);
      setNewFunnel({
        name: "",
        description: "",
        steps: [
          { name: "", path_pattern: "", order: 1 },
          { name: "", path_pattern: "", order: 2 }
        ]
      });
      fetchFunnels();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create funnel");
    }
  };

  const deleteFunnel = async (funnelId) => {
    if (!confirm("Are you sure you want to delete this funnel?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/analytics/funnels/${funnelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Funnel deleted");
      if (selectedFunnel === funnelId) {
        setSelectedFunnel(null);
        setFunnelAnalysis(null);
      }
      fetchFunnels();
    } catch (error) {
      toast.error("Failed to delete funnel");
    }
  };

  const addFunnelStep = () => {
    setNewFunnel(prev => ({
      ...prev,
      steps: [...prev.steps, { name: "", path_pattern: "", order: prev.steps.length + 1 }]
    }));
  };

  const removeFunnelStep = (index) => {
    if (newFunnel.steps.length <= 2) {
      toast.error("Funnel must have at least 2 steps");
      return;
    }
    setNewFunnel(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  const updateFunnelStep = (index, field, value) => {
    setNewFunnel(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const applyPreset = (preset) => {
    setNewFunnel({
      name: preset.name,
      description: preset.description,
      steps: preset.steps
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchRealtime()]);
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
            <Activity className="inline h-8 w-8 mr-2 text-primary" />
            Visitor Insights
          </h1>
          <p className="text-muted-foreground mt-1">
            Track visitor behavior, navigation patterns, and engagement
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedDays} onValueChange={setSelectedDays}>
            <SelectTrigger className="w-[140px]" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Realtime Banner */}
      {realtime && (
        <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">
                  {realtime.active_visitors} active visitor{realtime.active_visitors !== 1 ? 's' : ''} right now
                </span>
              </div>
              {realtime.visitors?.length > 0 && (
                <div className="flex gap-2">
                  {realtime.visitors.slice(0, 3).map((v, i) => (
                    <Badge key={i} variant="outline" className="bg-white">
                      {v.current_page}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="card-organic">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unique Visitors</p>
                <p className="text-2xl font-bold">{dashboard?.unique_visitors?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-organic">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Pageviews</p>
                <p className="text-2xl font-bold">{dashboard?.total_pageviews?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-organic">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg. Session Duration</p>
                <p className="text-2xl font-bold">{formatDuration(dashboard?.avg_session_duration_seconds)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-organic">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <Layers className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pages per Session</p>
                <p className="text-2xl font-bold">{dashboard?.avg_pages_per_session || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="overview-tab">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="funnels" data-testid="funnels-tab">
            <Target className="h-4 w-4 mr-2" />
            Funnels
          </TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="heatmap-tab">
            <MousePointer className="h-4 w-4 mr-2" />
            Heatmap
          </TabsTrigger>
          <TabsTrigger value="navigation" data-testid="navigation-tab">
            <ArrowRight className="h-4 w-4 mr-2" />
            Navigation Flow
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Daily Pageviews Chart */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <TrendingUp className="inline h-5 w-5 mr-2 text-primary" />
                  Daily Pageviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48 flex items-end gap-1">
                  {dashboard?.daily_pageviews?.slice(-14).map((day, i) => {
                    const max = Math.max(...dashboard.daily_pageviews.map(d => d.count));
                    const height = max > 0 ? (day.count / max) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                          title={`${day.date}: ${day.count} views`}
                        />
                        <span className="text-[10px] text-muted-foreground rotate-45 origin-left">
                          {day.date.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Device Breakdown */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <Monitor className="inline h-5 w-5 mr-2 text-primary" />
                  Device Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { key: 'desktop', icon: Monitor, label: 'Desktop', color: 'bg-blue-500' },
                    { key: 'tablet', icon: Tablet, label: 'Tablet', color: 'bg-purple-500' },
                    { key: 'mobile', icon: Smartphone, label: 'Mobile', color: 'bg-green-500' },
                  ].map(device => {
                    const count = dashboard?.devices?.[device.key] || 0;
                    const total = Object.values(dashboard?.devices || {}).reduce((a, b) => a + b, 0) || 1;
                    const percent = Math.round((count / total) * 100);
                    const DeviceIcon = device.icon;
                    
                    return (
                      <div key={device.key} className="flex items-center gap-3">
                        <DeviceIcon className="h-5 w-5 text-muted-foreground" />
                        <span className="w-20 text-sm">{device.label}</span>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full ${device.color}`} style={{ width: `${percent}%` }} />
                        </div>
                        <span className="w-12 text-sm text-right">{percent}%</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Top Pages */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <Eye className="inline h-5 w-5 mr-2 text-primary" />
                  Top Pages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.top_pages?.slice(0, 8).map((page, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm" title={page.url}>{page.url}</p>
                        {page.title && <p className="text-xs text-muted-foreground truncate">{page.title}</p>}
                      </div>
                      <Badge variant="outline">{page.views}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Referrers */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <Globe className="inline h-5 w-5 mr-2 text-primary" />
                  Traffic Sources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.referrers?.length > 0 ? dashboard.referrers.slice(0, 8).map((ref, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <p className="flex-1 truncate text-sm" title={ref.source}>{ref.source || 'Direct'}</p>
                      <Badge variant="outline">{ref.count}</Badge>
                    </div>
                  )) : (
                    <p className="text-muted-foreground text-sm">No referrer data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Funnels Tab */}
        <TabsContent value="funnels" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Conversion Funnels</h3>
              <p className="text-sm text-muted-foreground">Track user journeys and identify drop-off points</p>
            </div>
            <Button onClick={() => setFunnelDialogOpen(true)} data-testid="create-funnel-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Funnel
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Funnel List */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <Target className="inline h-5 w-5 mr-2 text-primary" />
                  Your Funnels
                </CardTitle>
              </CardHeader>
              <CardContent>
                {funnels.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No funnels created yet</p>
                    <p className="text-sm">Create your first conversion funnel</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {funnels.map((funnel) => (
                      <div
                        key={funnel.funnel_id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedFunnel === funnel.funnel_id 
                            ? 'bg-primary/10 border-primary' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => analyzeFunnel(funnel.funnel_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{funnel.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {funnel.steps.length} steps
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                analyzeFunnel(funnel.funnel_id);
                              }}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteFunnel(funnel.funnel_id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Funnel Analysis */}
            <Card className="card-organic lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <BarChart3 className="inline h-5 w-5 mr-2 text-primary" />
                  Funnel Analysis
                </CardTitle>
                {funnelAnalysis && (
                  <CardDescription>
                    {funnelAnalysis.funnel_name} - Last {selectedDays} days
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {analyzingFunnel ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : funnelAnalysis ? (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-3xl font-bold text-primary">{funnelAnalysis.total_entries}</p>
                        <p className="text-sm text-muted-foreground">Total Entries</p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-3xl font-bold text-green-600">{funnelAnalysis.total_completions}</p>
                        <p className="text-sm text-muted-foreground">Completions</p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-3xl font-bold text-blue-600">{funnelAnalysis.overall_conversion_rate}%</p>
                        <p className="text-sm text-muted-foreground">Conversion Rate</p>
                      </div>
                    </div>

                    {/* Funnel Visualization */}
                    <div className="space-y-2">
                      {funnelAnalysis.steps.map((step, i) => {
                        const widthPercent = funnelAnalysis.steps[0].sessions > 0 
                          ? (step.sessions / funnelAnalysis.steps[0].sessions) * 100 
                          : 0;
                        
                        return (
                          <div key={i}>
                            <div className="flex items-center gap-4 mb-1">
                              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                                {step.step}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{step.name}</span>
                                  <span className="text-sm">{step.sessions} sessions</span>
                                </div>
                                <div className="h-8 bg-muted rounded-lg overflow-hidden relative">
                                  <div 
                                    className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                                    style={{ width: `${widthPercent}%` }}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center text-sm font-medium">
                                    {step.conversion_from_start}% from start
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Drop-off indicator */}
                            {i < funnelAnalysis.steps.length - 1 && step.drop_off > 0 && (
                              <div className="flex items-center gap-4 py-2 ml-4">
                                <ArrowDown className="h-4 w-4 text-red-500" />
                                <span className="text-sm text-red-600">
                                  {step.drop_off} dropped ({funnelAnalysis.steps[i + 1]?.drop_off_rate || 0}% drop-off)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Select a funnel to analyze</p>
                    <p className="text-sm">Click on a funnel from the list or create a new one</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Heatmap Tab */}
        <TabsContent value="heatmap" className="space-y-6">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <MousePointer className="inline h-5 w-5 mr-2 text-primary" />
                Click Heatmap & Scroll Depth
              </CardTitle>
              <CardDescription>
                Visualize where users click and how far they scroll on each page
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <Input
                    placeholder="Enter page path (e.g., / or /quote)"
                    value={heatmapPage}
                    onChange={(e) => setHeatmapPage(e.target.value)}
                    data-testid="heatmap-page-input"
                  />
                </div>
                <Button onClick={() => fetchHeatmap(heatmapPage)} data-testid="load-heatmap-btn">
                  Load Heatmap
                </Button>
              </div>

              {heatmapData ? (
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Click Distribution</h4>
                    <HeatmapVisualization clicks={heatmapData.clicks || []} />
                  </div>
                  <div>
                    <h4 className="font-medium mb-3">Scroll Depth</h4>
                    {heatmapData.scroll_stats ? (
                      <>
                        <div className="mb-4 p-4 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground">Average Scroll Depth</p>
                          <p className="text-3xl font-bold text-primary">
                            {Math.round(heatmapData.scroll_stats.avg_scroll_depth || 0)}%
                          </p>
                        </div>
                        <ScrollDepthBar stats={heatmapData.scroll_stats} />
                      </>
                    ) : (
                      <p className="text-muted-foreground">No scroll data for this page</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <MousePointer className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Enter a page path and click "Load Heatmap" to see click data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Navigation Flow Tab */}
        <TabsContent value="navigation" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Entry Pages */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <ArrowRight className="inline h-5 w-5 mr-2 text-green-600" />
                  Entry Pages
                </CardTitle>
                <CardDescription>Where visitors first land on your site</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {navFlow?.entry_pages?.map((page, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                      <span className="text-lg font-bold text-muted-foreground w-6">{i + 1}</span>
                      <p className="flex-1 truncate text-sm">{page.page}</p>
                      <Badge variant="outline" className="bg-green-50">{page.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Exit Pages */}
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <ArrowRight className="inline h-5 w-5 mr-2 text-red-600 rotate-180" />
                  Exit Pages
                </CardTitle>
                <CardDescription>Where visitors leave your site</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {navFlow?.exit_pages?.map((page, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                      <span className="text-lg font-bold text-muted-foreground w-6">{i + 1}</span>
                      <p className="flex-1 truncate text-sm">{page.page}</p>
                      <Badge variant="outline" className="bg-red-50">{page.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Navigation Flows */}
            <Card className="card-organic lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  <ChevronRight className="inline h-5 w-5 mr-2 text-primary" />
                  Page Transitions
                </CardTitle>
                <CardDescription>How visitors navigate between pages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {navFlow?.flows?.slice(0, 12).map((flow, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <span className="text-xs truncate flex-1" title={flow.from}>
                        {flow.from === 'external' ? '🌐 External' : flow.from?.split('/').pop() || '/'}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-xs truncate flex-1" title={flow.to}>
                        {flow.to?.split('/').pop() || '/'}
                      </span>
                      <Badge variant="secondary" className="shrink-0">{flow.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Funnel Dialog */}
      <Dialog open={funnelDialogOpen} onOpenChange={setFunnelDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Target className="inline h-5 w-5 mr-2" />
              Create Conversion Funnel
            </DialogTitle>
            <DialogDescription>
              Define the steps users should take in your conversion flow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Presets */}
            {funnelPresets.length > 0 && (
              <div>
                <Label className="text-sm text-muted-foreground">Quick Start with Preset</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {funnelPresets.map((preset) => (
                    <Button
                      key={preset.id}
                      variant="outline"
                      size="sm"
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="funnel-name">Funnel Name *</Label>
              <Input
                id="funnel-name"
                value={newFunnel.name}
                onChange={(e) => setNewFunnel(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Quote to Policy"
                data-testid="funnel-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="funnel-description">Description</Label>
              <Textarea
                id="funnel-description"
                value={newFunnel.description}
                onChange={(e) => setNewFunnel(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this funnel tracks"
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Funnel Steps *</Label>
                <Button variant="outline" size="sm" onClick={addFunnelStep}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Step
                </Button>
              </div>
              
              {newFunnel.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0 mt-1">
                    {i + 1}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Step name (e.g., Landing Page)"
                      value={step.name}
                      onChange={(e) => updateFunnelStep(i, 'name', e.target.value)}
                      data-testid={`step-${i}-name`}
                    />
                    <Input
                      placeholder="URL pattern (e.g., /quote or ^/$)"
                      value={step.path_pattern}
                      onChange={(e) => updateFunnelStep(i, 'path_pattern', e.target.value)}
                      data-testid={`step-${i}-path`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive shrink-0"
                    onClick={() => removeFunnelStep(i)}
                    disabled={newFunnel.steps.length <= 2}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              <p className="text-xs text-muted-foreground">
                Tip: Use ^ for start and $ for end of URL. Example: ^/$ matches exactly "/"
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFunnelDialogOpen(false)}>Cancel</Button>
            <Button onClick={createFunnel} data-testid="submit-funnel-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Create Funnel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
